import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { convertToGeoLocation } from "@src/appV2/Location";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { PageWrapper } from "../../components/PageWrapper";
import { SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH } from "../path";
import { SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY } from "../ShiftDetails/constants";
import { PastShiftDetailsFooter } from "./components/PastShiftDetailsFooter";
import { PastShiftDetailsHeader } from "./components/PastShiftDetailsHeader";
import { type MissedPunchRequestStatus } from "./types";

interface PastShiftDetailsPageProps {
  data: {
    shift: WorkerShift;
    missedPunchRequestStatus: MissedPunchRequestStatus;
    isFullShiftPayment: boolean;
    isMissedPunchRequestEnabled: boolean;
    canSubmitTimesheet: boolean;
    requestedAmountInDollars?: number;
    totalPayInDollars: number;
    hourlyRateInDollars: number;
    requestExpiryTime?: string;
    requestId?: string;
  };
  dismissRequest: (requestId: string) => void;
}

export function PastShiftDetailsPage(props: PastShiftDetailsPageProps) {
  const { data, dismissRequest } = props;
  const {
    shift,
    missedPunchRequestStatus,
    isFullShiftPayment,
    isMissedPunchRequestEnabled,
    canSubmitTimesheet,
    requestedAmountInDollars,
    totalPayInDollars,
    hourlyRateInDollars,
    requestExpiryTime,
    requestId,
  } = data;

  const history = useHistory();
  const { userId } = useDefinedWorker();
  const shiftId = shift._id;
  const facilityTimeZone = shift.facility.tmz ?? DEFAULT_TIMEZONE;

  const workplaceLocation = shift.facility.geoLocation.coordinates
    ? convertToGeoLocation(shift.facility.geoLocation.coordinates)
    : undefined;

  const isTimesheetUploaded = (shift.timecard?.files?.length ?? 0) > 0;

  useLogEffect(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_PAGE_VIEWED, {
    shiftId,
  });

  const dateLabel = formatShortDateWithWeekday(shift.start, {
    timeZone: facilityTimeZone,
  });
  const qualificationName = shift.agentReq;

  const navigateToSubmitTimesheet = useCallback(
    (skipWorkplaceSignature: boolean) => {
      const searchParams = new URLSearchParams({
        [SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY]: String(skipWorkplaceSignature),
      });
      history.push({
        pathname: generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, { shiftId }),
        search: searchParams.toString(),
      });
    },
    [history, shiftId]
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PastShiftDetailsHeader
          shiftId={shiftId}
          dateLabel={dateLabel}
          qualificationName={qualificationName}
          missedPunchRequestStatus={missedPunchRequestStatus}
          isVerified={shift.verified ?? false}
          totalPayInDollars={totalPayInDollars}
          hourlyRateInDollars={hourlyRateInDollars}
          requestedAmountInDollars={requestedAmountInDollars}
          isFullShiftPayment={isFullShiftPayment}
        />
        {/* Placeholder for future shift detail content cards */}
        <Box sx={{ flexGrow: 1 }} />
        <PastShiftDetailsFooter
          shiftId={shiftId}
          shiftEnd={shift.end}
          workerId={userId}
          workplaceLocation={workplaceLocation}
          missedPunchRequestStatus={missedPunchRequestStatus}
          isVerified={shift.verified ?? false}
          isFullShiftPayment={isFullShiftPayment}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          isTimesheetUploaded={isTimesheetUploaded}
          canSubmitTimesheet={canSubmitTimesheet}
          requestExpiryTime={requestExpiryTime}
          facilityTimeZone={facilityTimeZone}
          requestId={requestId}
          navigateToSubmitTimesheet={navigateToSubmitTimesheet}
          dismissRequest={dismissRequest}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
